import isNil from 'lodash/fp/isNil';
import { useCallback, useEffect, useState, type ChangeEvent, type FocusEvent } from 'react';
import Input, { type InputProps } from '@/components/common/Input';

export type NumberProps = InputProps & {
    precision?: number;
};

const Number = ({ precision = 0, value, onChange, onBlur, ...props }: NumberProps) => {
    const [inputValue, setInputValue] = useState(isNil(value) ? '' : value);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            if (isNil(value)) {
                setInputValue('');
            } else {
                const num = parseFloat(String(value));
                setInputValue(globalThis.Number.isNaN(num) ? '' : num.toFixed(precision));
            }
        }
    }, [isFocused, precision, value]);

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;
            // Convert empty string to null to satisfy Zod validation schemas
            // that expect number or null (z.number().nullable()) but not empty string
            if (value === '') {
                setInputValue(value);
                onChange?.(null as any);
                return;
            }

            const isValidNumber = new RegExp(`^\\d*(\\.\\d{0,${precision}})?$`).test(value);
            if (isValidNumber) {
                setInputValue(value);
                onChange?.(parseFloat(value) as any);
            }
        },
        [onChange, precision]
    );

    const handelBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            if (inputValue === '') {
                setInputValue?.('');
            } else {
                const num = parseFloat(inputValue as string);
                setInputValue?.(globalThis.Number.isNaN(num) ? '' : num.toFixed(precision));
            }
            setIsFocused(false);
            onBlur?.(e);
        },
        [inputValue, onBlur, precision]
    );

    const handleFocus = useCallback(() => {
        setIsFocused(true);
    }, []);

    return (
        <Input
            {...props}
            onBlur={handelBlur}
            onFocus={handleFocus as any}
            onChange={handleChange}
            type="text"
            value={inputValue}
        />
    );
};
export default Number;
